import React, { useEffect, useState } from 'react';
import { Container, Row, Col, ToastContainer, Toast } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import API_CONFIG from '../API_CONFIG/API_CONFIG';
import axios from 'axios';
import { 
  Eye, EyeOff, CheckCircle, XCircle, Shield, Lock, 
  Key, Sparkles, ArrowRight, Fingerprint, AlertTriangle, RefreshCw 
} from 'lucide-react';

const UpPassWord = ({ id }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState({
    password: false, Newpassword: false, NewpasswordRepeat: false
  });
  
  const [formData, setFormData] = useState({ 
    id, password: '', Newpassword: '', NewpasswordRepeat: ''
  });

  const [passwordErrors, setPasswordErrors] = useState({
    length: false, uppercase: false, lowercase: false, number: false, special: false, match: true
  });

  const [formValid, setFormValid] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [toast, setToast] = useState({ show: false, message: '', variant: '' });

  // Validation & Force
  useEffect(() => {
    const { Newpassword, NewpasswordRepeat } = formData;
    const errors = {
      length: Newpassword.length < 8,
      uppercase: !/[A-Z]/.test(Newpassword),
      lowercase: !/[a-z]/.test(Newpassword),
      number: !/[0-9]/.test(Newpassword),
      special: !/[!@#$%^&*(),.?":{}|<>]/.test(Newpassword),
      match: Newpassword !== NewpasswordRepeat && NewpasswordRepeat.length > 0
    };
    setPasswordErrors(errors);
    
    const metRequirements = Object.values(errors).filter(e => !e).length;
    setPasswordStrength((metRequirements / 6) * 100);
    
    const isPasswordValid = Object.values(errors).every(error => !error);
    setFormValid(isPasswordValid && formData.password && Newpassword);
  }, [formData]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const formDataToSend = new FormData();
    Object.entries(formData).forEach(([key, value]) => formDataToSend.append(key, value));

    try {
      const response = await axios.post(`${API_CONFIG.apiUrl}UpPasse.php`, formDataToSend);
      setToast({ show: true, message: response.data.result || "Succès !", variant: 'success' });
      setFormData({ id, password: '', Newpassword: '', NewpasswordRepeat: '' });
    } catch (error) {
      setToast({ show: true, message: "Erreur de connexion au serveur.", variant: 'danger' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStrengthDetails = () => {
    if (passwordStrength < 40) return { color: '#ff4d4d', label: 'Faible', width: '25%' };
    if (passwordStrength < 70) return { color: '#ffa502', label: 'Moyen', width: '60%' };
    if (passwordStrength < 100) return { color: '#2ed573', label: 'Sécurisé', width: '85%' };
    return { color: '#1e90ff', label: 'Excellent', width: '100%' };
  };

  return (
    <div className="premium-viewport">
      <style>{`
        :root {
          --bg-dark: #05070a;
          --glass-bg: rgba(13, 17, 28, 0.7);
          --accent-blue: #3b82f6;
          --accent-purple: #8b5cf6;
          --text-main: #f8fafc;
          --text-dim: #94a3b8;
        }

        .premium-viewport {
          min-height: 100vh;
          background: var(--bg-dark);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          position: relative;
          overflow: hidden;
        }

        /* Arrière-plan animé */
        .mesh-gradient {
          position: absolute;
          width: 100%;
          height: 100%;
          background: 
            radial-gradient(at 0% 0%, rgba(59, 130, 246, 0.15) 0px, transparent 50%),
            radial-gradient(at 100% 100%, rgba(139, 92, 246, 0.15) 0px, transparent 50%),
            radial-gradient(at 50% 50%, rgba(14, 165, 233, 0.05) 0px, transparent 50%);
          filter: blur(80px);
          z-index: 0;
        }

        .premium-card {
          background: var(--glass-bg);
          backdrop-filter: blur(25px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 32px;
          padding: 40px;
          width: 100%;
          max-width: 500px;
          position: relative;
          z-index: 1;
          box-shadow: 0 20px 50px rgba(0,0,0,0.5);
        }

        .premium-input-group {
          margin-bottom: 20px;
        }

        .input-wrapper {
          position: relative;
          margin-top: 8px;
        }

        .premium-input {
          width: 100%;
          background: rgba(0, 0, 0, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 14px 16px;
          padding-right: 45px;
          color: white;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .premium-input:focus {
          outline: none;
          border-color: var(--accent-blue);
          background: rgba(59, 130, 246, 0.05);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.15);
        }

        .visibility-toggle {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: var(--text-dim);
          cursor: pointer;
          display: flex;
        }

        .strength-meter-container {
          margin: 20px 0;
          background: rgba(255, 255, 255, 0.05);
          height: 6px;
          border-radius: 10px;
          overflow: hidden;
        }

        .strength-bar-fill {
          height: 100%;
          transition: all 0.6s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .requirement-item {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 0.85rem;
          margin-bottom: 6px;
          color: var(--text-dim);
        }

        .requirement-item.met { color: #2ed573; }
        
        .submit-button {
          width: 100%;
          padding: 16px;
          border-radius: 16px;
          border: none;
          background: linear-gradient(135deg, var(--accent-blue), var(--accent-purple));
          color: white;
          font-weight: 600;
          margin-top: 20px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          transition: transform 0.2s;
        }

        .submit-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          filter: grayscale(0.5);
        }

        .submit-button:active { transform: scale(0.98); }

        .toast-custom {
          background: rgba(15, 23, 42, 0.9) !important;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255,255,255,0.1) !important;
          border-radius: 16px !important;
          color: white !important;
        }
      `}</style>

      <div className="mesh-gradient" />

      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="premium-card"
      >
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="p-2" style={{ background: 'rgba(59, 130, 246, 0.1)', borderRadius: '12px' }}>
            <Shield className="text-primary" size={24} />
          </div>
          <Sparkles className="text-warning" size={20} />
        </div>

        <h2 className="mb-2 fw-bold" style={{ letterSpacing: '-0.5px' }}>Sécuriser votre compte</h2>
        <p className="text-muted small mb-4">Mettez à jour vos identifiants pour une protection maximale.</p>

        <form onSubmit={handleSubmit}>
          {/* Mot de passe actuel */}
          <div className="premium-input-group">
            <label className="small fw-medium text-dim px-1">Mot de passe actuel</label>
            <div className="input-wrapper">
              <input
                type={showPassword.password ? "text" : "password"}
                name="password"
                className="premium-input"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                required
              />
              <button 
                type="button" 
                className="visibility-toggle"
                onClick={() => setShowPassword({ ...showPassword, password: !showPassword.password })}
              >
                {showPassword.password ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div style={{ height: '1px', background: 'rgba(255,255,255,0.05)', margin: '25px 0' }} />

          {/* Nouveau mot de passe */}
          <div className="premium-input-group">
            <label className="small fw-medium text-dim px-1">Nouveau mot de passe</label>
            <div className="input-wrapper">
              <input
                type={showPassword.Newpassword ? "text" : "password"}
                name="Newpassword"
                className="premium-input"
                placeholder="Nouveau mot de passe"
                value={formData.Newpassword}
                onChange={handleChange}
                required
              />
              <button 
                type="button" 
                className="visibility-toggle"
                onClick={() => setShowPassword({ ...showPassword, Newpassword: !showPassword.Newpassword })}
              >
                {showPassword.Newpassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Indicateur de force */}
          <AnimatePresence>
            {formData.Newpassword && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <div className="strength-meter-container">
                  <motion.div 
                    className="strength-bar-fill"
                    animate={{ 
                      width: getStrengthDetails().width,
                      backgroundColor: getStrengthDetails().color 
                    }}
                  />
                </div>
                <div className="d-flex justify-content-between mb-3">
                  <span className="small text-muted">Force: <b style={{ color: getStrengthDetails().color }}>{getStrengthDetails().label}</b></span>
                </div>

                <div className="mb-4">
                  {[
                    { key: 'length', text: '8+ caractères' },
                    { key: 'uppercase', text: 'Une majuscule' },
                    { key: 'special', text: 'Un caractère spécial' }
                  ].map(req => (
                    <div key={req.key} className={`requirement-item ${!passwordErrors[req.key] ? 'met' : ''}`}>
                      {!passwordErrors[req.key] ? <CheckCircle size={14} /> : <XCircle size={14} opacity={0.5}/>}
                      {req.text}
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="premium-input-group">
            <label className="small fw-medium text-dim px-1">Confirmer le mot de passe</label>
            <div className="input-wrapper">
              <input
                type={showPassword.NewpasswordRepeat ? "text" : "password"}
                name="NewpasswordRepeat"
                className="premium-input"
                placeholder="Répéter le mot de passe"
                value={formData.NewpasswordRepeat}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <motion.button 
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="submit-button"
            disabled={!formValid || isSubmitting}
            type="submit"
          >
            {isSubmitting ? (
              <RefreshCw className="spin" size={20} />
            ) : (
              <>
                <span>Mettre à jour</span>
                <ArrowRight size={18} />
              </>
            )}
          </motion.button>
        </form>
      </motion.div>

      {/* Notifications Toast */}
      <ToastContainer position="bottom-center" className="pb-4">
        <Toast 
          show={toast.show} 
          onClose={() => setToast({ ...toast, show: false })}
          delay={4000}
          autohide
          className="toast-custom"
        >
          <Toast.Body className="d-flex align-items-center gap-3">
            {toast.variant === 'success' ? <CheckCircle color="#2ed573" /> : <AlertTriangle color="#ff4757" />}
            {toast.message}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default UpPassWord;